import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { User, Phone, Mail, Gift, Package, Calendar, MapPin, Edit2, Save, X, MessageCircle, Wine, AlertTriangle, ArrowRight, Printer, PlusCircle } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { adminService } from '../../services/adminService';
import { promoSummerOrderService, PROMO_STATUS_LABELS, PROMO_STATUS_COLORS } from '../../services/promoSummerOrderService';

const AdminClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoOrders, setPromoOrders] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  
  const [isEditing, setIsEditing] = useState(location?.state?.openEdit || false);
  const [whatsappOptInLoading, setWhatsappOptInLoading] = useState(false);
  const [whatsappSuccessMessage, setWhatsappSuccessMessage] = useState('');
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', address: '', city: '', postalCode: '' });

  useEffect(() => {
    loadClientDetails();
  }, [clientId]);

  useEffect(() => {
    if (clientId) loadPromoOrders();
  }, [clientId]);

  const loadPromoOrders = async () => {
    try {
      setPromoLoading(true);
      const data = await promoSummerOrderService?.getByClientId(clientId);
      setPromoOrders(data);
    } catch (err) {
      console.error('Erreur chargement commandes promo:', err);
    } finally {
      setPromoLoading(false);
    }
  };

  const loadClientDetails = async () => {
    // ✅ VÉRIFICATION SIMPLE: clientId existe et n'est pas vide
    if (!clientId || clientId?.trim() === '') {
      setError('Aucun identifiant client fourni.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await adminService?.getClientDetails(clientId);
      setClient(data);
      setEditForm({
        name: data?.name || '',
        email: data?.email || '',
        phone: data?.phone || '',
        address: data?.address || '',
        city: data?.city || '',
        postalCode: data?.postalCode || ''
      });
    } catch (err) {
      console.error('Erreur lors du chargement des détails client:', err);
      // ✅ MESSAGE D'ERREUR CONVIVIAL
      if (err?.message?.includes('Client non trouvé')) {
        setError('Aucun client trouvé pour cet identifiant.');
      } else {
        setError(err?.message || 'Erreur lors du chargement des détails du client');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Handle WhatsApp opt-in
  const handleWhatsAppOptIn = async () => {
    try {
      setWhatsappOptInLoading(true);
      setError(null);
      setWhatsappSuccessMessage('');

      await adminService?.enableWhatsAppNotifications(clientId);

      // Update local state to hide button immediately
      setClient({ ...client, whatsappOptIn: true, whatsappOptInAt: new Date()?.toISOString() });

      // Show success message
      setWhatsappSuccessMessage('Vous recevrez désormais les notifications WhatsApp liées à vos envois.');

      // Hide success message after 5 seconds
      setTimeout(() => {
        setWhatsappSuccessMessage('');
      }, 5000);
    } catch (err) {
      console.error('Erreur lors de l\'activation des notifications WhatsApp:', err);
      setError(err?.message || 'Erreur lors de l\'activation des notifications WhatsApp');
    } finally {
      setWhatsappOptInLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/admin-login');
  };

  const handleSaveClient = async () => {
    try {
      setLoading(true);
      await adminService?.updateClient(clientId, editForm);
      setClient({ ...client, ...editForm, postalCode: editForm?.postalCode });
      setIsEditing(false);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du client:', err);
      setError(err?.message || 'Erreur lors de la mise à jour du client');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_transit': 'bg-blue-100 text-blue-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors?.[status] || 'bg-gray-100 text-gray-800';
  };

  // ✅ AFFICHAGE ÉTAT DE CHARGEMENT
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des détails du client...</p>
        </div>
      </div>
    );
  }

  // ✅ AFFICHAGE ERREUR AVEC MESSAGE CLAIR ET BOUTON RETOUR
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-start mb-4">
            <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-red-800 font-semibold mb-2">Client non trouvé</h3>
              <p className="text-red-700 text-sm mb-4">{error}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/customer-management-system')}
            className="w-full text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Retour à la liste des clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation onLogout={handleLogout} />
      <BreadcrumbTrail customItems={[
        { label: 'Administration', path: '/admin-shipments-dashboard' },
        { label: 'Clients', path: '/customer-management-system' },
        { label: client?.name || 'Détail', path: `/admin-client-detail?id=${clientId}` }
      ]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ✅ NEW: Success message banner */}
        {whatsappSuccessMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <MessageCircle className="w-5 h-5 text-green-600 mr-3" />
              <p className="text-green-800 font-medium">{whatsappSuccessMessage}</p>
            </div>
          </div>
        )}

        {/* Client Information Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <User className="w-6 h-6 text-primary-600" />
              Informations Client
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/create-new-shipment', { state: { clientId: clientId } })}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
              >
                <PlusCircle className="w-4 h-4" />
                Créer un envoi
              </button>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Modifier
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveClient}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm({
                        name: client?.name,
                        email: client?.email || '',
                        phone: client?.phone,
                        address: client?.address || '',
                        city: client?.city || '',
                        postalCode: client?.postalCode || ''
                      });
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm?.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              ) : (
                <p className="text-gray-900">{client?.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Phone className="w-4 h-4" />
                Téléphone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editForm?.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              ) : (
                <p className="text-gray-900">{client?.phone}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={editForm?.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              ) : (
                <p className="text-gray-900">{client?.email || '—'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                Adresse
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm?.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Adresse complète du client"
                />
              ) : (
                <p className="text-gray-900">{client?.address || '—'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm?.city}
                  onChange={(e) => setEditForm({ ...editForm, city: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Ville"
                />
              ) : (
                <p className="text-gray-900">{client?.city || '—'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm?.postalCode}
                  onChange={(e) => setEditForm({ ...editForm, postalCode: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Code postal"
                />
              ) : (
                <p className="text-gray-900">{client?.postalCode || '—'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Gift className="w-4 h-4" />
                Points Fidélité
              </label>
              <p className="text-2xl font-bold text-primary-600">{client?.loyaltyPoints || 0}</p>
            </div>
          </div>

          {/* Save button at bottom of form when editing */}
          {isEditing && (
            <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={handleSaveClient}
                disabled={loading}
                className="inline-flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({
                    name: client?.name,
                    email: client?.email || '',
                    phone: client?.phone,
                    address: client?.address || '',
                    city: client?.city || '',
                    postalCode: client?.postalCode || ''
                  });
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </button>
            </div>
          )}

          {/* ✅ NEW: WhatsApp Opt-In Button Section */}
          {!client?.whatsappOptIn && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={handleWhatsAppOptIn}
                disabled={whatsappOptInLoading}
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {whatsappOptInLoading ? 'Activation en cours...' : 'Recevoir les notifications WhatsApp'}
              </button>
              <p className="mt-2 text-sm text-gray-600">
                Activez les notifications WhatsApp pour recevoir des mises à jour sur vos envois.
              </p>
            </div>
          )}
        </div>

        {/* Promo Summer Orders Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Wine className="w-5 h-5 text-amber-600" />
              Commandes Promo Été 2026
              <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                {promoOrders?.length || 0}
              </span>
            </h3>
            <button
              onClick={() => navigate('/promo-summer-orders')}
              className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
            >
              Voir tout <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {promoLoading ? (
            <div className="py-4 text-center text-gray-400 text-sm">Chargement...</div>
          ) : promoOrders?.length === 0 ? (
            <p className="text-center py-6 text-gray-500 text-sm">Aucune commande promo pour ce client</p>
          ) : (
            <div className="space-y-3">
              {promoOrders?.map((order) => {
                const statusLabel = PROMO_STATUS_LABELS?.[order?.order_status] || order?.order_status;
                const statusColor = PROMO_STATUS_COLORS?.[order?.order_status] || 'bg-gray-100 text-gray-700';
                const qty = order?.barrel_quantity || 1;
                const totalPaid = (order?.deposit_paid ? qty * 50 : 0) + (order?.balance_paid ? qty * 110 : 0);
                const totalDue = qty * 160 - totalPaid;
                return (
                  <div key={order?.id} className="p-4 border border-amber-100 rounded-lg bg-amber-50/50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Wine className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-gray-900">{qty} barrique{qty > 1 ? 's' : ''} 270L</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                          {statusLabel}
                        </span>
                      </div>
                      {order?.order_status === 'relance_necessaire' && (
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-600 mb-3">
                      <div>
                        <span className="text-gray-400 block">Payé</span>
                        <span className="font-semibold text-green-600">{totalPaid}€</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Reste à payer</span>
                        <span className="font-semibold text-blue-600">{totalDue}€</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Livraison</span>
                        <span className="font-medium">
                          {order?.delivery_date ? new Date(order.delivery_date)?.toLocaleDateString('fr-FR') : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Créé le</span>
                        <span className="font-medium">{new Date(order?.created_at)?.toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate('/promo-summer-orders')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-md text-xs font-medium hover:bg-amber-200 transition-colors"
                      >
                        <Printer className="w-3 h-3" /> Facture acompte
                      </button>
                      <button
                        onClick={() => navigate('/promo-summer-orders')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-md text-xs font-medium hover:bg-red-200 transition-colors"
                      >
                        <AlertTriangle className="w-3 h-3" /> Relancer client
                      </button>
                      <button
                        onClick={() => navigate('/promo-summer-orders')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-medium hover:bg-purple-200 transition-colors"
                      >
                        <Calendar className="w-3 h-3" /> Planifier collecte
                      </button>
                      {!['converti_en_envoi', 'affecte_conteneur']?.includes(order?.order_status) && (
                        <button
                          onClick={() => navigate('/promo-summer-orders')}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-200 transition-colors"
                        >
                          <ArrowRight className="w-3 h-3" /> Convertir en envoi
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Shipment History */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-primary-600" />
            Historique des Envois
            <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
              {client?.shipments?.length || 0}
            </span>
          </h3>

          {client?.shipments?.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Aucun envoi trouvé pour ce client</p>
          ) : (
            <div className="space-y-3">
              {client?.shipments?.map((shipment) => (
                <div 
                  key={shipment?.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/shipment-detail-management/${shipment?.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-gray-900">{shipment?.reference}</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(shipment?.status)}`}>
                      {shipment?.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Vers: {shipment?.receiverName}, {shipment?.receiverCity}
                    </div>
                    <div className="flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      Total: {shipment?.totalAmount?.toFixed(2) || '0.00'}€
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(shipment.createdAt)?.toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClientDetail;